<!--
  The session screen: one exercise, one set, full screen.

  The big number still dominates - that part of the plan holds up. What it now
  also shows is every set of the exercise you are on, done and still to come,
  so you can see 8x20 easy, 8x20 hard without holding it in your head between
  sets. It deliberately stops at the current exercise: the rest of the session
  is still not shown, because seeing "and then four more exercises" at set two
  is the thing that makes people put the dumbbell down.
-->
<script lang="ts">
  import { app } from '../lib/state.svelte';
  import { kg, loadLabel, mmss } from '../lib/format';
  import type { Effort, SkipReason } from '../lib/types';

  const q = $derived(app.current);

  // Adjusted reps and weight for the set on screen. Both start at the plan's
  // target, so the normal case is one tap and nothing else.
  let repsOverride = $state<number | null>(null);
  let weightOverride = $state<number | null>(null);
  let showSkip = $state(false);

  // Reset the overrides whenever the screen moves to a different set.
  let shownKey = $state('');
  $effect(() => {
    const key = q ? `${q.exercise_id}:${q.set_index}` : '';
    if (key !== shownKey) {
      shownKey = key;
      repsOverride = null;
      weightOverride = null;
      showSkip = false;
    }
  });

  const reps = $derived(repsOverride ?? q?.target_reps ?? null);
  const weight = $derived(weightOverride ?? q?.target_weight_kg ?? null);
  const step = $derived(q?.load_step_kg ?? 1.5);
  const isTimed = $derived(q?.ui_type === 'timer');
  const resting = $derived(app.restRemaining > 0);

  const EFFORT = ['', 'easy', 'hard', 'failed'];

  /** Every set of the exercise on screen: done, current, and still to come. */
  const rows = $derived.by(() => {
    if (!q) return [];
    const done = new Map(
      app.logged.filter((s) => s.exercise_id === q.exercise_id).map((s) => [s.set_index, s]),
    );
    return Array.from({ length: q.set_total }, (_, i) => {
      const index = i + 1;
      const set = done.get(index);
      return {
        index,
        state: set ? ('done' as const) : index === q.set_index ? ('now' as const) : ('todo' as const),
        set,
      };
    });
  });

  function bumpReps(by: number) {
    repsOverride = Math.max(0, (reps ?? 0) + by);
  }

  function bumpWeight(by: number) {
    const next = Math.max(q?.load_min_kg ?? 0, (weight ?? 0) + by * step);
    weightOverride = q?.load_max_kg != null ? Math.min(q.load_max_kg, next) : next;
  }

  function logEffort(effort: Effort) {
    if (!q) return;
    app.log(effort, {
      reps: isTimed ? null : reps,
      weight: q.load_unit === 'none' ? null : weight,
      time: isTimed ? Math.round(app.elapsed) : null,
    });
  }

  function doSkip(reason: SkipReason) {
    showSkip = false;
    app.skip(reason);
  }
</script>

{#if q}
  <div class="session">
    <header>
      <span class="progress">{q.exercise_position} of {q.exercise_count}</span>
      <button class="end" onclick={() => app.endEarly()}>End</button>
    </header>

    {#if resting}
      <!-- Rest runs over the next set, so he can see what is coming. -->
      <div class="rest" role="timer" aria-live="off">
        <span class="rest-label">rest</span>
        <span class="rest-time">{mmss(app.restRemaining)}</span>
        <button onclick={() => app.addRest(30)}>+30s</button>
        <button onclick={() => app.skipRest()}>skip</button>
      </div>
    {/if}

    <main>
      <p class="exercise">{q.exercise_name}</p>

      {#if isTimed}
        <div class="target">
          <span class="big">{mmss(app.timerStartedAt === null ? 0 : app.elapsed)}</span>
        </div>
        <p class="unit">
          {q.target_time_seconds ? `target ${mmss(q.target_time_seconds)}` : 'max hold'}
        </p>
      {:else}
        <div class="target">
          <button class="nudge" onclick={() => bumpReps(-1)} aria-label="one rep fewer">−</button>
          <span class="big">{reps ?? '-'}</span>
          <button class="nudge" onclick={() => bumpReps(1)} aria-label="one rep more">+</button>
        </div>
        <p class="unit">reps{q.per_side ? ' each side' : ''}</p>
      {/if}

      {#if q.load_unit !== 'none'}
        <div class="weight">
          <button class="nudge" onclick={() => bumpWeight(-1)} aria-label="lighter">−</button>
          <span class="weight-value">{kg(weight)}</span>
          <button class="nudge" onclick={() => bumpWeight(1)} aria-label="heavier">+</button>
        </div>
        <p class="unit">{loadLabel(weight, q.load_unit)}</p>
      {/if}

      {#if q.cue}
        <p class="cue">{q.cue}</p>
      {/if}
    </main>

    <ol class="sets" aria-label="sets for this exercise">
      {#each rows as row (row.index)}
        <li class={row.state}>
          <span class="n">{row.index}</span>
          {#if row.set}
            <span class="val">
              {#if row.set.time_seconds != null}
                {row.set.time_seconds}s
              {:else if !row.set.completed}
                skipped{row.set.skip_reason ? ` · ${row.set.skip_reason.replace('_', ' ')}` : ''}
              {:else}
                {row.set.reps ?? '-'}{row.set.weight_kg != null
                  ? ` × ${kg(row.set.weight_kg)} kg`
                  : ''}
              {/if}
            </span>
            {#if row.set.effort}
              <span class="tag e{row.set.effort}">{EFFORT[row.set.effort]}</span>
            {/if}
          {:else}
            <span class="val">
              {#if isTimed}
                {q.target_time_seconds ? mmss(q.target_time_seconds) : 'max'}
              {:else}
                {q.target_reps ?? '-'}{q.target_weight_kg != null
                  ? ` × ${kg(q.target_weight_kg)} kg`
                  : ''}
              {/if}
            </span>
            {#if row.state === 'now'}<span class="tag now-tag">now</span>{/if}
          {/if}
        </li>
      {/each}
    </ol>

    <footer>
      {#if isTimed && app.timerStartedAt === null}
        <button class="primary" onclick={() => app.startTimer()}>Start the hold</button>
      {:else}
        <!-- Effort, bought for one tap. This is the data the old app never had. -->
        <div class="efforts">
          <button class="effort easy" onclick={() => logEffort(1)}>Easy</button>
          <button class="effort hard" onclick={() => logEffort(2)}>Hard</button>
          <button class="effort failed" onclick={() => logEffort(3)}>Failed</button>
        </div>
      {/if}

      {#if showSkip}
        <div class="skip-reasons">
          <button onclick={() => doSkip('too_heavy')}>too heavy</button>
          <button onclick={() => doSkip('pain')}>it hurts</button>
          <button onclick={() => doSkip('out_of_time')}>no time</button>
          <button onclick={() => doSkip('other')}>other</button>
        </div>
      {:else}
        <button class="skip" onclick={() => (showSkip = true)}>skip this set</button>
      {/if}
    </footer>
  </div>
{:else}
  <p class="empty">Nothing left in this session.</p>
{/if}

<style>
  .session {
    display: flex;
    flex-direction: column;
    min-height: 100dvh;
    padding: env(safe-area-inset-top) 16px calc(env(safe-area-inset-bottom) + 16px);
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0;
  }

  .progress {
    color: var(--dim);
    font-size: 0.9rem;
    letter-spacing: 0.04em;
  }

  .end {
    background: none;
    border: 0;
    color: var(--dim);
    font-size: 0.9rem;
    padding: 8px;
  }

  .rest {
    display: flex;
    align-items: center;
    gap: 10px;
    background: var(--accent-soft);
    border: 1px solid var(--line);
    border-radius: var(--radius);
    padding: 10px 14px;
    margin-bottom: 8px;
  }

  .rest-label {
    color: var(--accent-text);
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 600;
  }

  .rest-time {
    font-variant-numeric: tabular-nums;
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--accent-text);
    margin-right: auto;
  }

  .rest button {
    background: var(--panel);
    border: 1px solid var(--line-strong);
    border-radius: 999px;
    color: var(--text);
    font-size: 0.85rem;
    padding: 6px 12px;
  }

  main {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 2px;
    padding: 8px 0;
  }

  .exercise {
    font-size: 1.15rem;
    font-weight: 600;
    margin: 0 0 14px;
  }

  .target {
    display: flex;
    align-items: center;
    gap: 18px;
  }

  .big {
    font-size: 5.5rem;
    font-weight: 700;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  .weight {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 12px;
  }

  .weight-value {
    font-size: 2.4rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .nudge {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    border: 1px solid var(--line-strong);
    background: var(--panel);
    color: var(--text);
    font-size: 1.6rem;
    line-height: 1;
  }

  .unit {
    color: var(--dim);
    font-size: 0.9rem;
    margin: 6px 0 0;
  }

  .cue {
    margin: 16px 0 0;
    color: var(--accent-text);
    font-size: 0.98rem;
    max-width: 22rem;
  }

  /* --- the set list ------------------------------------------------- */

  .sets {
    list-style: none;
    margin: 0 0 12px;
    padding: 6px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: var(--radius);
  }

  .sets li {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 8px;
    border-radius: var(--radius-sm);
    font-size: 0.92rem;
    font-variant-numeric: tabular-nums;
  }

  .sets .n {
    color: var(--faint);
    font-size: 0.78rem;
    min-width: 1.1rem;
  }

  .sets .val {
    flex: 1;
  }

  .sets li.todo {
    color: var(--faint);
  }

  .sets li.now {
    background: var(--accent-soft);
    color: var(--accent-text);
    font-weight: 600;
  }

  .sets li.done .val {
    color: var(--text);
  }

  .tag {
    font-size: 0.72rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .now-tag {
    color: var(--accent-text);
  }
  .e1 {
    color: var(--easy);
  }
  .e2 {
    color: var(--hard);
  }
  .e3 {
    color: var(--failed);
  }

  /* --- actions ------------------------------------------------------ */

  footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .efforts {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
  }

  /* Thumb-sized: he is holding a dumbbell in the other hand. */
  .effort {
    min-height: 74px;
    border-radius: var(--radius);
    border: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--on-accent);
  }

  .easy {
    background: var(--easy);
  }
  .hard {
    background: var(--hard);
  }
  .failed {
    background: var(--failed);
  }

  .primary {
    min-height: 74px;
    border-radius: var(--radius);
    border: 0;
    background: var(--accent);
    color: var(--on-accent);
    font-size: 1.1rem;
    font-weight: 600;
  }

  .skip,
  .skip-reasons button {
    background: none;
    border: 0;
    color: var(--dim);
    font-size: 0.9rem;
    padding: 10px;
  }

  .skip-reasons {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 4px;
  }

  .skip-reasons button {
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: var(--radius-sm);
    padding: 12px 4px;
  }

  .empty {
    padding: 40px 16px;
    color: var(--dim);
  }
</style>
